import { useEffect, useRef, useState } from "react";
import {
  Query,
  QueryClient,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  answerDocumentCraftingRun,
  cancelDocumentCraftingRun,
  createDocumentCraftingDocument,
  createDocumentCraftingLabel,
  createDocumentCraftingRevision,
  deleteDocumentCraftingDocument,
  deleteDocumentCraftingLabel,
  discardDocumentCraftingDraft,
  duplicateDocumentCraftingRevision,
  getDocumentCraftingBlob,
  getDocumentCraftingCapabilities,
  getDocumentCraftingDocument,
  getDocumentCraftingDocuments,
  getDocumentCraftingLabels,
  getDocumentCraftingMembers,
  getDocumentCraftingModels,
  getDocumentCraftingMySettings,
  getDocumentCraftingRevision,
  getDocumentCraftingRun,
  getDocumentCraftingShares,
  getDocumentCraftingTypes,
  getDocumentCraftingUsage,
  getDocumentCraftingWorkspaceSettings,
  insertDocumentCraftingDiagram,
  refineDocumentCraftingDraft,
  requestDocumentCraftingTranslation,
  retryDocumentCraftingRun,
  saveDocumentCraftingDraft,
  setDocumentCraftingDocumentLabels,
  updateDocumentCraftingDocument,
  updateDocumentCraftingLabel,
  updateDocumentCraftingMySettings,
  updateDocumentCraftingShares,
  updateDocumentCraftingWorkspaceSettings,
  uploadDocumentCraftingRevision,
} from "../Api/documentCrafting";
import {
  DocumentCraftingAnswersPayload,
  DocumentCraftingCreatePayload,
  DocumentCraftingDocumentDetail,
  DocumentCraftingDocumentLabelsUpdate,
  DocumentCraftingDocumentListParams,
  DocumentCraftingDocumentUpdate,
  DocumentCraftingFiles,
  DocumentCraftingLabelCreate,
  DocumentCraftingLabelUpdate,
  DocumentCraftingLanguage,
  DocumentCraftingMySettings,
  DocumentCraftingRefinePayload,
  DocumentCraftingRevisePayload,
  DocumentCraftingRun,
  DocumentCraftingRunBrief,
  DocumentCraftingRunStatus,
  DocumentCraftingSharesUpdate,
  DocumentCraftingUploadRevisionPayload,
  DocumentCraftingWorkspaceSettingsUpdate,
  isRunTerminal,
} from "../Models/documentCrafting";

const RUN_POLL_MS = 2000;
// Lists and details refresh while one of the caller's runs is working.
const ACTIVE_RUN_POLL_MS = 5000;
const MODULE_STALE_MS = 5 * 60 * 1000;

/** Every key starts with "documentCrafting", so a workspace switch clears the
 * module with one predicate (`isDocumentCraftingQuery`). */
export const documentCraftingKeys = {
  capabilities: ["documentCraftingCapabilities"] as const,
  types: ["documentCraftingTypes"] as const,
  models: ["documentCraftingModels"] as const,
  members: (q: string) => ["documentCraftingMembers", q] as const,
  documents: ["documentCraftingDocuments"] as const,
  documentList: (params: DocumentCraftingDocumentListParams) =>
    ["documentCraftingDocuments", params] as const,
  document: (documentId: number | undefined) =>
    ["documentCraftingDocument", documentId] as const,
  revision: (documentId: number | undefined, revisionId: number | undefined) =>
    ["documentCraftingRevision", documentId, revisionId] as const,
  run: (runId: number | undefined) => ["documentCraftingRun", runId] as const,
  labels: ["documentCraftingLabels"] as const,
  labelList: (includeArchived: boolean) =>
    ["documentCraftingLabels", includeArchived] as const,
  shares: (documentId: number | undefined) =>
    ["documentCraftingShares", documentId] as const,
  usage: (documentId: number | undefined) =>
    ["documentCraftingUsage", documentId] as const,
  mySettings: ["documentCraftingSettings", "me"] as const,
  workspaceSettings: ["documentCraftingSettings", "workspace"] as const,
  duplicate: (documentId: number | undefined, revisionId: number | undefined) =>
    ["documentCraftingDuplicate", documentId, revisionId] as const,
  images: ["documentCraftingImage"] as const,
  image: (url: string) => ["documentCraftingImage", url] as const,
};

export const isDocumentCraftingQuery = (query: Query) =>
  String(query.queryKey[0]).startsWith("documentCrafting");

const isWorking = (run: DocumentCraftingRunBrief | null | undefined) =>
  !!run &&
  !isRunTerminal(run.status) &&
  run.status !== DocumentCraftingRunStatus.AWAITING_ANSWERS;

// Images of one revision, including its translation previews.
const invalidateRevisionImages = (
  queryClient: QueryClient,
  documentId: number,
  revisionId: number,
) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === documentCraftingKeys.images[0] &&
      String(query.queryKey[1]).includes(
        `/documents/${documentId}/revisions/${revisionId}/`,
      ),
  });

const invalidateDocument = (queryClient: QueryClient, documentId: number) => {
  queryClient.invalidateQueries({
    queryKey: documentCraftingKeys.document(documentId),
  });
  queryClient.invalidateQueries({ queryKey: documentCraftingKeys.documents });
};

// Module

export const useDocumentCraftingCapabilities = () =>
  useQuery({
    queryKey: documentCraftingKeys.capabilities,
    queryFn: getDocumentCraftingCapabilities,
    staleTime: MODULE_STALE_MS,
  });

export const useDocumentCraftingTypes = () =>
  useQuery({
    queryKey: documentCraftingKeys.types,
    queryFn: getDocumentCraftingTypes,
    staleTime: MODULE_STALE_MS,
  });

export const useDocumentCraftingModels = () =>
  useQuery({
    queryKey: documentCraftingKeys.models,
    queryFn: getDocumentCraftingModels,
    staleTime: MODULE_STALE_MS,
  });

/** Debounce `q` in the caller; an empty `q` lists the first members. */
export const useDocumentCraftingMembers = (q: string, enabled = true) =>
  useQuery({
    queryKey: documentCraftingKeys.members(q),
    queryFn: () => getDocumentCraftingMembers(q),
    placeholderData: keepPreviousData,
    staleTime: MODULE_STALE_MS,
    enabled,
  });

// Documents

export const useDocumentCraftingDocuments = (
  params: DocumentCraftingDocumentListParams,
) =>
  useQuery({
    queryKey: documentCraftingKeys.documentList(params),
    queryFn: () => getDocumentCraftingDocuments(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.status !== "error" &&
      query.state.data?.documents.some((item) => isWorking(item.active_run))
        ? ACTIVE_RUN_POLL_MS
        : false,
  });

export const useDocumentCraftingDocument = (documentId: number | undefined) =>
  useQuery({
    queryKey: documentCraftingKeys.document(documentId),
    queryFn: () => getDocumentCraftingDocument(documentId as number),
    enabled: !!documentId,
    refetchInterval: (query) =>
      query.state.status !== "error" && isWorking(query.state.data?.active_run)
        ? ACTIVE_RUN_POLL_MS
        : false,
  });

// Per query client: the last READY run seen per revision ("<doc>:<rev>").
// A refine renders the same draft revision again, so its file URLs stay the
// same; a newer READY run means the cached page PNGs and PDF are outdated.
const renderedRuns = new WeakMap<QueryClient, Map<string, number>>();

/** Invalidates a revision's images when its `last_run` is a READY run newer
 * than the one seen before, also when that run ended while no run page was
 * open (useDocumentCraftingRun only covers the run it watches). */
const useRefreshImagesAfterRun = (
  queryClient: QueryClient,
  documentId: number | undefined,
  revisionId: number | undefined,
  readyRunId: number | null,
) => {
  useEffect(() => {
    if (!documentId || !revisionId || readyRunId === null) return;
    let seen = renderedRuns.get(queryClient);
    if (!seen) {
      seen = new Map();
      renderedRuns.set(queryClient, seen);
    }
    const key = `${documentId}:${revisionId}`;
    const previous = seen.get(key);
    seen.set(key, readyRunId);
    if (previous !== undefined && previous !== readyRunId) {
      invalidateRevisionImages(queryClient, documentId, revisionId);
    }
  }, [queryClient, documentId, revisionId, readyRunId]);
};

export const useDocumentCraftingRevision = (
  documentId: number | undefined,
  revisionId: number | undefined,
) => {
  const queryClient = useQueryClient();
  const revision = useQuery({
    queryKey: documentCraftingKeys.revision(documentId, revisionId),
    queryFn: () =>
      getDocumentCraftingRevision(documentId as number, revisionId as number),
    enabled: !!documentId && !!revisionId,
    refetchInterval: (query) =>
      query.state.status !== "error" && isWorking(query.state.data?.last_run)
        ? ACTIVE_RUN_POLL_MS
        : false,
  });
  const lastRun =
    revision.data && revision.data.id === revisionId ? revision.data.last_run : null;
  useRefreshImagesAfterRun(
    queryClient,
    documentId,
    revisionId,
    lastRun && lastRun.status === DocumentCraftingRunStatus.READY ? lastRun.id : null,
  );
  return revision;
};

export const useDocumentCraftingShares = (
  documentId: number | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: documentCraftingKeys.shares(documentId),
    queryFn: () => getDocumentCraftingShares(documentId as number),
    enabled: enabled && !!documentId,
  });

export const useDocumentCraftingUsage = (
  documentId: number | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: documentCraftingKeys.usage(documentId),
    queryFn: () => getDocumentCraftingUsage(documentId as number),
    enabled: enabled && !!documentId,
  });

/** The prefilled create form for "Duplicate as new document" (a POST that
 * changes nothing, loaded like a query by the create page). */
export const useDocumentCraftingDuplicatePrefill = (
  documentId: number | undefined,
  revisionId: number | undefined,
) =>
  useQuery({
    queryKey: documentCraftingKeys.duplicate(documentId, revisionId),
    queryFn: () =>
      duplicateDocumentCraftingRevision(
        documentId as number,
        revisionId as number,
      ),
    enabled: !!documentId && !!revisionId,
    staleTime: 0,
    gcTime: 0,
  });

const useDocumentDetailMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<DocumentCraftingDocumentDetail>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (document) => {
      queryClient.setQueryData(
        documentCraftingKeys.document(document.id),
        document,
      );
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.documents,
      });
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.shares(document.id),
      });
    },
  });
};

export const useCreateDocumentCraftingDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      payload: DocumentCraftingCreatePayload;
      files?: DocumentCraftingFiles;
    }) => createDocumentCraftingDocument(variables.payload, variables.files),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.documents,
      }),
  });
};

/** Title (edit), visibility, workspace permission and owner (manage). */
export const useUpdateDocumentCraftingDocument = () =>
  useDocumentDetailMutation(
    (variables: { documentId: number; data: DocumentCraftingDocumentUpdate }) =>
      updateDocumentCraftingDocument(variables.documentId, variables.data),
  );

export const useDeleteDocumentCraftingDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) =>
      deleteDocumentCraftingDocument(documentId),
    onSuccess: (_, documentId) => {
      queryClient.removeQueries({
        queryKey: documentCraftingKeys.document(documentId),
      });
      queryClient.removeQueries({
        queryKey: documentCraftingKeys.shares(documentId),
      });
      queryClient.removeQueries({
        queryKey: documentCraftingKeys.usage(documentId),
      });
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.documents,
      });
      // Usage counts of project labels change.
      queryClient.invalidateQueries({ queryKey: documentCraftingKeys.labels });
    },
  });
};

export const useUpdateDocumentCraftingShares = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      data: DocumentCraftingSharesUpdate;
    }) => updateDocumentCraftingShares(variables.documentId, variables.data),
    onSuccess: (shares, variables) => {
      queryClient.setQueryData(
        documentCraftingKeys.shares(variables.documentId),
        shares,
      );
      invalidateDocument(queryClient, variables.documentId);
    },
  });
};

export const useSetDocumentCraftingDocumentLabels = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      data: DocumentCraftingDocumentLabelsUpdate;
    }) =>
      setDocumentCraftingDocumentLabels(variables.documentId, variables.data),
    onSuccess: (labels, variables) => {
      queryClient.setQueryData<DocumentCraftingDocumentDetail>(
        documentCraftingKeys.document(variables.documentId),
        (document) => (document ? { ...document, ...labels } : document),
      );
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.documents,
      });
      queryClient.invalidateQueries({ queryKey: documentCraftingKeys.labels });
    },
  });
};

// Labels

export const useDocumentCraftingLabels = (includeArchived = false) =>
  useQuery({
    queryKey: documentCraftingKeys.labelList(includeArchived),
    queryFn: () => getDocumentCraftingLabels(includeArchived),
  });

// Renames, colours and deletions show on every document carrying the label.
const useLabelMutation = <TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentCraftingKeys.labels });
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.documents,
      });
      queryClient.invalidateQueries({
        queryKey: ["documentCraftingDocument"],
      });
    },
  });
};

export const useCreateDocumentCraftingLabel = () =>
  useLabelMutation((data: DocumentCraftingLabelCreate) =>
    createDocumentCraftingLabel(data),
  );

export const useUpdateDocumentCraftingLabel = () =>
  useLabelMutation(
    (variables: { labelId: number; data: DocumentCraftingLabelUpdate }) =>
      updateDocumentCraftingLabel(variables.labelId, variables.data),
  );

export const useDeleteDocumentCraftingLabel = () =>
  useLabelMutation((labelId: number) => deleteDocumentCraftingLabel(labelId));

// Settings

export const useDocumentCraftingMySettings = () =>
  useQuery({
    queryKey: documentCraftingKeys.mySettings,
    queryFn: getDocumentCraftingMySettings,
  });

export const useUpdateDocumentCraftingMySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      data: Partial<DocumentCraftingMySettings>;
      notify?: boolean;
    }) => updateDocumentCraftingMySettings(variables.data, variables.notify),
    onSuccess: (settings) =>
      queryClient.setQueryData(documentCraftingKeys.mySettings, settings),
  });
};

export const useDocumentCraftingWorkspaceSettings = () =>
  useQuery({
    queryKey: documentCraftingKeys.workspaceSettings,
    queryFn: getDocumentCraftingWorkspaceSettings,
  });

export const useUpdateDocumentCraftingWorkspaceSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DocumentCraftingWorkspaceSettingsUpdate) =>
      updateDocumentCraftingWorkspaceSettings(data),
    onSuccess: (settings) => {
      queryClient.setQueryData(
        documentCraftingKeys.workspaceSettings,
        settings,
      );
      // The default model is part of both.
      queryClient.invalidateQueries({ queryKey: documentCraftingKeys.models });
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.capabilities,
      });
    },
  });
};

// Revisions and drafts

/** 409 `draft_exists`: read `draft_revision_id` with
 * `getDocumentCraftingError(error)`. */
export const useCreateDocumentCraftingRevision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      payload: DocumentCraftingRevisePayload;
      files?: DocumentCraftingFiles;
    }) =>
      createDocumentCraftingRevision(
        variables.documentId,
        variables.payload,
        variables.files,
      ),
    onSuccess: (_, variables) =>
      invalidateDocument(queryClient, variables.documentId),
  });
};

export const useUploadDocumentCraftingRevision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      payload: DocumentCraftingUploadRevisionPayload;
    }) => uploadDocumentCraftingRevision(variables.documentId, variables.payload),
    onSuccess: (_, variables) =>
      invalidateDocument(queryClient, variables.documentId),
  });
};

/** 409 `save_conflict`: read the details with `getDocumentCraftingError`,
 * then save again with `force: true` if the user agrees. */
export const useSaveDocumentCraftingDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      revisionId: number;
      force?: boolean;
    }) =>
      saveDocumentCraftingDraft(
        variables.documentId,
        variables.revisionId,
        variables.force,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.revision(
          variables.documentId,
          variables.revisionId,
        ),
      });
      invalidateDocument(queryClient, variables.documentId);
    },
  });
};

export const useInsertDocumentCraftingDiagram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { documentId: number; revisionId: number; targetDocumentId: number }) =>
      insertDocumentCraftingDiagram(variables.documentId, variables.revisionId, {
        target_document_id: variables.targetDocumentId,
      }),
    onSuccess: (result) => invalidateDocument(queryClient, result.document_id),
  });
};

export const useRefineDocumentCraftingDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      revisionId: number;
      payload: DocumentCraftingRefinePayload;
    }) =>
      refineDocumentCraftingDraft(
        variables.documentId,
        variables.revisionId,
        variables.payload,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.revision(
          variables.documentId,
          variables.revisionId,
        ),
      });
      invalidateDocument(queryClient, variables.documentId);
    },
  });
};

export const useDiscardDocumentCraftingDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { documentId: number; revisionId: number }) =>
      discardDocumentCraftingDraft(variables.documentId, variables.revisionId),
    onSuccess: (_, variables) => {
      queryClient.removeQueries({
        queryKey: documentCraftingKeys.revision(
          variables.documentId,
          variables.revisionId,
        ),
      });
      invalidateDocument(queryClient, variables.documentId);
    },
  });
};

export const useRequestDocumentCraftingTranslation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: number;
      revisionId: number;
      language: DocumentCraftingLanguage;
    }) =>
      requestDocumentCraftingTranslation(
        variables.documentId,
        variables.revisionId,
        variables.language,
      ),
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.revision(
          variables.documentId,
          variables.revisionId,
        ),
      }),
  });
};

// Runs

/**
 * A run, polled every 2 s while it works. Polling pauses while the run waits
 * for answers and stops on errors; the answer and cancel mutations put the
 * new state into the cache, which restarts it. When the run ends, the
 * document, the list and the images of its revision are refreshed.
 */
export const useDocumentCraftingRun = (runId: number | undefined) => {
  const queryClient = useQueryClient();
  const run = useQuery({
    queryKey: documentCraftingKeys.run(runId),
    queryFn: () => getDocumentCraftingRun(runId as number),
    enabled: !!runId,
    staleTime: 0,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const status = query.state.data?.status;
      return status === undefined ||
        (!isRunTerminal(status) &&
          status !== DocumentCraftingRunStatus.AWAITING_ANSWERS)
        ? RUN_POLL_MS
        : false;
    },
  });

  const status = run.data?.status;
  const documentId = run.data?.document_id;
  const revisionId = run.data?.revision_id;
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && isRunTerminal(status) && documentId) {
      invalidateDocument(queryClient, documentId);
      if (revisionId) {
        queryClient.invalidateQueries({
          queryKey: documentCraftingKeys.revision(documentId, revisionId),
        });
        if (status === DocumentCraftingRunStatus.READY) {
          invalidateRevisionImages(queryClient, documentId, revisionId);
        }
      }
      queryClient.invalidateQueries({
        queryKey: documentCraftingKeys.usage(documentId),
      });
    }
    wasActive.current = status !== undefined && !isRunTerminal(status);
  }, [status, documentId, revisionId, queryClient]);

  return run;
};

const useRunStateMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<DocumentCraftingRun>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (run) => {
      queryClient.setQueryData(documentCraftingKeys.run(run.id), run);
      invalidateDocument(queryClient, run.document_id);
    },
  });
};

/** 400 when a question is neither answered nor delegated. */
export const useAnswerDocumentCraftingRun = () =>
  useRunStateMutation(
    (variables: { runId: number; payload: DocumentCraftingAnswersPayload }) =>
      answerDocumentCraftingRun(variables.runId, variables.payload),
  );

export const useCancelDocumentCraftingRun = () =>
  useRunStateMutation((runId: number) => cancelDocumentCraftingRun(runId));

/** Starts a new run of the same kind; navigate to the returned `run_id`. */
export const useRetryDocumentCraftingRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) => retryDocumentCraftingRun(runId),
    onSuccess: (_, runId) => {
      const failed = queryClient.getQueryData<DocumentCraftingRun>(
        documentCraftingKeys.run(runId),
      );
      if (failed) invalidateDocument(queryClient, failed.document_id);
      else
        queryClient.invalidateQueries({
          queryKey: documentCraftingKeys.documents,
        });
    },
  });
};

// Authenticated images

/**
 * An image of the module (thumbnail, page or slide PNG) as an object URL.
 * `<img src>` cannot send the bearer token, so the file is fetched through
 * `call`; the Blob is cached by URL and the object URL is revoked when the
 * component unmounts or the image changes. Pass `enabled: false` to wait,
 * e.g. until the image scrolls into view (`AuthImage` does that).
 */
export const useDocumentCraftingImage = (
  url: string | null | undefined,
  enabled = true,
) => {
  const blob = useQuery({
    queryKey: documentCraftingKeys.image(url ?? ""),
    queryFn: () => getDocumentCraftingBlob(url as string),
    enabled: enabled && !!url,
    // Files of a revision only change when a run renders it again, which
    // invalidates them (useDocumentCraftingRun, useDocumentCraftingRevision).
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

  const data = blob.data;
  const [objectUrl, setObjectUrl] = useState<{ blob: Blob; src: string }>();
  useEffect(() => {
    if (!data) return undefined;
    const src = URL.createObjectURL(data);
    setObjectUrl({ blob: data, src });
    return () => URL.revokeObjectURL(src);
  }, [data]);

  const src = data && objectUrl?.blob === data ? objectUrl.src : null;
  return {
    src,
    isLoading: enabled && !!url && !blob.isError && !src,
    isError: blob.isError,
    refetch: blob.refetch,
  };
};

/** The same for any file of the module, e.g. a PDF for an `<iframe>`
 * (`PdfPreviewModal` cannot send the admin workspace header). */
export const useDocumentCraftingFileObjectUrl = useDocumentCraftingImage;
